import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PrimaryButton, Stack, Text } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { LoadingOverlay } from "cayo.ui";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { IFormField } from "@cayo/shared/api/types/schema";
import logger from "@cayo/shared/libs/logger";
import { commonMessages } from "@cayo/shared/messages/common";
import { Row } from "@cayo/shared/components/CommonLayout";
import { ICommandBarItem } from "../../api/schema.api";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import { regexs } from "../../utils/regexs";
import { commandBarMessages } from "../CommandBar/messages";
import { DispatchAction } from "@cayo/shared/components/GridContainer/types";
import { GridContainer } from "../GridContainer/GridContainer";
import Panel from "../Panel/Panel";
import useOdataScheme from "../Schemes/ODataScheme/hook";

const log = logger.getLogger("SQPicker");

type DialogConfig = {
  previewQueryId: string | undefined;
  hidden: boolean;
};

type SQPickerProps = {
  filter: string; // ? target object ?
  isPreviewImmediate: boolean;
  dispatch: DispatchAction;
  onClose: () => void;
  setDialogConfig: (config: DialogConfig) => void;
  previewQueryId?: string | null;
  setAbortControllerState: (props: AbortController | null) => void;
};

type QuickSearchButtonAnnotation = IFormField & { icon?: string };

export default function SQPicker(props: SQPickerProps) {
  const [selectedQueryId, SetSelectedQueryId] = useState<string>("");
  const [openPanel, { setFalse: closePanel }] = useBoolean(true);

  const intl = useIntl();
  const { isLoading, propertyAnnotations, scheme } = useOdataScheme("configuration/savedQueries");

  const previewQueryObject = {
    key: "Preview",
    itemType: "normal",
    split: true,
    text: intl.formatMessage(commonMessages.previewQuery),
    icon: { iconName: "RedEye", type: "icon" },
    action: "savedQueries.preview",
    multiselect: false,
    dependsOnSelection: true,
    type: "command-bar-item",
    onClick: () => {
      if (tokenStorage.isInLocalStorage()) {
        let currentUrl = window.location.href;
        if (!currentUrl.includes("previewQueryId")) {
          currentUrl += `?previewQueryId=${selectedQueryId}`;
        } else {
          currentUrl = currentUrl.replace(regexs.previewQueryIdRegex, `$1${selectedQueryId}`);
        }
        window.open(currentUrl, "_blank");
      } else {
        props.setDialogConfig({
          hidden: false,
          previewQueryId: selectedQueryId,
        });
      }
    },
  } as ICommandBarItem;

  const initialized = useRef(false);
  log.debug("render -> props, initialized", props, initialized.current);

  if (!initialized.current && !isLoading && !!scheme?.detailsList) {
    scheme.detailsList.selectionMode = "single";
    scheme.detailsList.url = makeUrl(scheme.detailsList.url, props.filter);

    //remove Export
    if (scheme.commandBar) {
      scheme.commandBar.items = scheme.commandBar?.items?.filter(
        (itm) => itm.key !== "cayo.graph.export"
      );

      const edit = scheme.commandBar.items?.find((itm) => itm.key === "Properties");
      if (edit) {
        edit.action = "savedQueries.edit";
      }
      const deleteBtn = scheme.commandBar.items?.find((itm) => itm.key === "Delete");
      if (deleteBtn) {
        deleteBtn.bindings = { disabled: "builtIn" };
        deleteBtn.action = "savedQueries.delete";
        delete deleteBtn.ajaxAction;
      }

      const addBtn = scheme.commandBar.items?.find((itm) => itm.key === "Add");
      if (addBtn) {
        addBtn.action = "savedQueries.add";
      }
    }

    if (scheme.filterBar) {
      scheme.filterBar.annotations = [
        {
          label: "Favorites",
          shortName: "Favorites",
          dataType: "Edm.String",
          type: "toggle",
          name: "advancedFilter",
          modelProperty: "isFavorite eq true",
          icon: "FavoriteStar",
        } as QuickSearchButtonAnnotation, //TODO: hack need quickFilterBarPicker
      ];
    }

    // add preview command
    if (scheme.commandBar) {
      scheme.commandBar?.items?.push(previewQueryObject);
    }
    // add "switchProperty": "isFavorite" to favorites column
    const favColumn = scheme.detailsList.columns?.find((c) => c.key === "isFavorite");
    if (favColumn) {
      favColumn.renderOptions!.switchProperty = "isFavorite";
      log.debug("favColumn -> ", favColumn);
    }

    initialized.current = true;
  }

  // update preview command
  if (!isLoading && !!scheme?.detailsList && scheme?.commandBar) {
    scheme.commandBar.items = scheme.commandBar?.items?.map((i) => {
      if (i.key === "Preview") {
        return previewQueryObject;
      }

      return i;
    });
  }

  const onSelectQuery = useCallback(
    (item: Entity | undefined) => {
      SetSelectedQueryId(item?.id ?? "");
      props.isPreviewImmediate &&
        props.dispatch({ kind: "selectQuery", previewQuery: true, queryId: item?.id ?? "" });
    },
    [props.dispatch]
  );

  const onItemSelected = () => {
    const controller: AbortController = new AbortController();
    props.setAbortControllerState(controller);
    props.dispatch({
      kind: "selectQuery",
      queryId: selectedQueryId,
      previewQuery: !!props.previewQueryId,
      selectOnPreviewMode: !!props.previewQueryId,
      props: {
        signal: controller.signal,
        callback: () => {
          props.setAbortControllerState(null);
        },
      },
    });
    closePanel();
  };

  return (
    <Panel
      isOpen={openPanel}
      isBlocking={false}
      type={"medium"}
      hideCloseButton={true}
      onClose={props.onClose}
    >
      <Stack horizontal={false} verticalFill={true}>
        <Row valign={true} justifyContent="flex-start" style={titleStyle}>
          <Text variant="large">{intl.formatMessage(commandBarMessages.browseAll)}</Text>
        </Row>
        {isLoading && <LoadingOverlay />}
        {initialized.current && (
          <GridContainer
            scheme={scheme!}
            propertyAnnotations={propertyAnnotations}
            gridSettingsKey={`savedQuery${scheme?.detailsList?.id}`}
            onSelect={onSelectQuery}
            dispatchOuter={props.dispatch}
            onItemClick={onItemSelected}
          />
        )}
        <Stack horizontalAlign="end" verticalAlign={"end"} tokens={{ padding: 20 }}>
          <PrimaryButton disabled={!selectedQueryId} onClick={onItemSelected}>
            {intl.formatMessage(commonMessages.select)}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Panel>
  );
}

function makeUrl(url: string, filter: string) {
  return url + "&$filter=" + filter;
}

const titleStyle: React.CSSProperties = {
  padding: "20px 40px 10px 20px",
  position: "relative",
  overflow: "hidden",
  width: "calc(100% - 70px)",
};
